import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, PackageCheck, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Stars } from "@/components/reviews/Stars";
import { toast } from "sonner";

export default function MyReviewsPage() {
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState(null);
  const [awaiting, setAwaiting] = useState(null);

  const loadMine = () => api.get("/reviews/mine").then((r) => setMine(r.data.reviews)).catch(() => setMine([]));
  const loadAwaiting = () => api.get("/reviews/awaiting").then((r) => setAwaiting(r.data.awaiting)).catch(() => setAwaiting([]));
  useEffect(() => { loadMine(); loadAwaiting(); }, []);

  const del = async (id) => {
    try { await api.delete(`/reviews/${id}`); toast.success("Review deleted"); loadMine(); loadAwaiting(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="my-reviews-page">
      <PageHeader title="My Reviews" icon={Star} subtitle="Reviews you've written and products waiting for your feedback." />
      <div className="mb-4 flex gap-2">
        <button onClick={() => setTab("mine")} data-testid="tab-mine" className={`rounded-full px-4 py-2 text-sm font-medium ${tab === "mine" ? "brand-gradient-bg text-white" : "border border-slate-200 text-slate-600"}`}>My reviews {mine ? `(${mine.length})` : ""}</button>
        <button onClick={() => setTab("awaiting")} data-testid="tab-awaiting" className={`rounded-full px-4 py-2 text-sm font-medium ${tab === "awaiting" ? "brand-gradient-bg text-white" : "border border-slate-200 text-slate-600"}`}>Awaiting review {awaiting ? `(${awaiting.length})` : ""}</button>
      </div>

      {tab === "mine" && (
        <div className="space-y-3" data-testid="my-reviews-list">
          {mine?.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">You haven't written any reviews yet.</p>}
          {(mine || []).map((r) => (
            <div key={r.review_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`my-review-${r.review_id}`}>
              <div className="flex items-start gap-3">
                {r.product_image ? <img src={r.product_image} alt="" className="h-14 w-14 rounded-lg object-cover" /> : <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-slate-100"><PackageCheck className="h-5 w-5 text-slate-300" /></div>}
                <div className="min-w-0 flex-1">
                  <Link to={`/product/${r.slug || r.product_id}`} className="text-sm font-semibold text-slate-800 hover:text-blue-600">{r.product_name || "Product"}</Link>
                  <div className="mt-0.5 flex items-center gap-2"><Stars value={r.rating} size={13} /><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium capitalize text-slate-500">{r.status}</span></div>
                  {r.title && <p className="mt-1 text-sm font-medium text-slate-700">{r.title}</p>}
                  {r.body && <p className="text-sm text-slate-500">{r.body}</p>}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Link to={`/product/${r.slug || r.product_id}`} title="Edit on product page" className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:text-blue-500"><Pencil className="h-4 w-4" /></Link>
                  <button onClick={() => del(r.review_id)} data-testid={`delete-review-${r.review_id}`} className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "awaiting" && (
        <div className="space-y-3" data-testid="awaiting-list">
          {awaiting?.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">No delivered products awaiting your review.</p>}
          {(awaiting || []).map((a) => (
            <div key={a.product_id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`awaiting-${a.product_id}`}>
              {a.product_image ? <img src={a.product_image} alt="" className="h-14 w-14 rounded-lg object-cover" /> : <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-slate-100"><PackageCheck className="h-5 w-5 text-slate-300" /></div>}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800">{a.product_name || "Product"}</p>
                <p className="text-xs text-slate-400">Order {a.number}</p>
              </div>
              <Link to={`/product/${a.slug || a.product_id}`} data-testid={`write-${a.product_id}`} className="rounded-full brand-gradient-bg px-4 py-2 text-xs font-semibold text-white">Write a review</Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
